import React, { useState, useEffect } from "react";
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useGetMySubmissionsMutation } from "state/api";
import { toast } from "react-toastify";

const MySubmissions = () => {
  const { user } = useSelector((s) => s.auth);
  const [getMySubmissions] = useGetMySubmissionsMutation();
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({ rx: [], weekly: [], psp: [], lycored: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      setLoading(true);
      getMySubmissions({ EmpNo: user.id })
        .unwrap()
        .then(setData)
        .catch((err) => toast.error(err?.data?.message || "Failed to load"))
        .finally(() => setLoading(false));
    }
  }, [user, getMySubmissions]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>My Submissions</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Rx (${data.rx.length})`} />
        <Tab label={`Weekly (${data.weekly.length})`} />
        <Tab label={`PSP (${data.psp.length})`} />
        <Tab label={`Lycored (${data.lycored.length})`} />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Doctor</TableCell><TableCell>Category</TableCell><TableCell>Visit Date</TableCell>
              <TableCell>New Rx</TableCell><TableCell>Samples</TableCell><TableCell>POB</TableCell><TableCell>Date</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.rx.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.DoctName}</TableCell>
                  <TableCell>{r.DocCategory || "-"}</TableCell>
                  <TableCell>{r.visit_date?.split("T")[0]}</TableCell>
                  <TableCell>{r.new_rx_count}</TableCell>
                  <TableCell>{r.folireg_samples_given}</TableCell>
                  <TableCell>{r.pob_strips}</TableCell>
                  <TableCell>{r.submitted_at?.split("T")[0]}</TableCell>
                </TableRow>
              ))}
              {data.rx.length === 0 && <TableRow><TableCell colSpan={7} align="center">No submissions yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Week Start</TableCell><TableCell>Chemist Stock</TableCell><TableCell>Total Strips</TableCell>
              <TableCell>POB Strips</TableCell><TableCell>Prescribers</TableCell><TableCell>Date</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.weekly.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>{w.week_start_date?.split("T")[0]}</TableCell>
                  <TableCell>{w.chemist_stock_count}</TableCell>
                  <TableCell>{w.total_strips_at_chemist}</TableCell>
                  <TableCell>{w.pob_taken_strips}</TableCell>
                  <TableCell>{w.prescriber_count}</TableCell>
                  <TableCell>{w.submitted_at?.split("T")[0]}</TableCell>
                </TableRow>
              ))}
              {data.weekly.length === 0 && <TableRow><TableCell colSpan={6} align="center">No submissions yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 2 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Doctor</TableCell><TableCell>PSP Kits</TableCell><TableCell>Date Given</TableCell>
              <TableCell>New Rx w/ PSP</TableCell><TableCell>Free Strips</TableCell><TableCell>Date</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.psp.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.DoctName}</TableCell>
                  <TableCell>{p.psp_kits_given}</TableCell>
                  <TableCell>{p.date_given?.split("T")[0]}</TableCell>
                  <TableCell>{p.new_rx_with_psp}</TableCell>
                  <TableCell>{p.free_strips_given}</TableCell>
                  <TableCell>{p.submitted_at?.split("T")[0]}</TableCell>
                </TableRow>
              ))}
              {data.psp.length === 0 && <TableRow><TableCell colSpan={6} align="center">No submissions yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 3 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Doctor</TableCell><TableCell>IVF</TableCell><TableCell>Potential</TableCell>
              <TableCell>Available</TableCell><TableCell>Strips</TableCell><TableCell>Bonanza</TableCell><TableCell>Date</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.lycored.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.DoctName}</TableCell>
                  <TableCell><Chip size="small" label={l.practicing_in_ivf ? "Yes" : "No"} color={l.practicing_in_ivf ? "success" : "default"} /></TableCell>
                  <TableCell>{l.monthly_potential}</TableCell>
                  <TableCell><Chip size="small" label={l.lycored_m_available ? "Yes" : "No"} color={l.lycored_m_available ? "success" : "default"} /></TableCell>
                  <TableCell>{l.strips_available}</TableCell>
                  <TableCell><Chip size="small" label={l.bonanza_given ? "Yes" : "No"} color={l.bonanza_given ? "info" : "default"} /></TableCell>
                  <TableCell>{l.submitted_at?.split("T")[0]}</TableCell>
                </TableRow>
              ))}
              {data.lycored.length === 0 && <TableRow><TableCell colSpan={7} align="center">No submissions yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MySubmissions;
